const DEFAULT_EVENT_FALLBACK_SUFFIX = "/guests/guestlist";

/**
 * Sub-routes that contain a dynamic ID after the route name.
 * When switching events, the dynamic ID belongs to the old event
 * and must be stripped so we land on the list page instead.
 * "slotting" stays for the legacy redirect routes; "programme" strips the
 * offering sub-route (/programme/offering/:offeringId → /programme) so an
 * event switch never lands on a foreign event's offering board.
 */
const ROUTES_WITH_DYNAMIC_CHILD = ["slotting", "seating", "programme"];

/**
 * Builds the target URL when switching between events.
 * Keeps the current event sub-route when possible, otherwise falls back to guest list.
 */
export function getEventSwitchHref(
  targetEventId: string,
  pathname?: string | null,
): string {
  const fallbackHref = `/event/${targetEventId}${DEFAULT_EVENT_FALLBACK_SUFFIX}`;

  if (!pathname) return fallbackHref;

  const pathWithoutQuery = pathname.split("?")[0]?.split("#")[0] ?? "";
  const pathSegments = pathWithoutQuery.split("/").filter(Boolean);

  const isEventRoute = pathSegments[0] === "event";
  const suffixSegments = isEventRoute ? pathSegments.slice(2) : [];

  if (suffixSegments.length === 0) return fallbackHref;

  // Access sub-pages that name one of the old event's records
  // (`settings/access/members/:eventRoleId`, `settings/access/roles/:templateId`)
  // land on their list; `settings/access/invite` and the tabs carry no id and stay.
  if (suffixSegments[0] === "settings" && suffixSegments[1] === "access" && suffixSegments.length > 3) {
    const list = suffixSegments[2] === "roles" ? "/roles" : "";
    return `/event/${targetEventId}/settings/access${list}`;
  }

  // Strip dynamic child IDs that belong to the old event
  if (
    suffixSegments.length > 1 &&
    ROUTES_WITH_DYNAMIC_CHILD.includes(suffixSegments[0])
  ) {
    return `/event/${targetEventId}/${suffixSegments[0]}`;
  }

  return `/event/${targetEventId}/${suffixSegments.join("/")}`;
}

